import React, { useCallback, useEffect, useState } from 'react';
import { Typography, Input, Select, Space, Spin, Alert, Modal } from 'antd';
import { message } from '../../../services/toastMessage';
import { IconShieldCog } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { getAdminRoleUsers, setAdminUserRole, IAdminRoleUser } from '../../../services/AdminService';
import EmptyState from '../../../components/EmptyState';
import AdminResponsiveList from '../../../components/Admin/AdminResponsiveList';
import { TRANSLATIONS } from '../../../constants/translations';
import { translate } from '../../../helpers/translationHelper';
import { textSorter, dateSorter } from '../../../helpers/adminTableHelpers';

const { Title, Text } = Typography;
const { Search } = Input;

const ROLE_OPTIONS = ['User', 'Moderator', 'Admin'];

const ROLE_LABEL_KEYS: Record<string, string> = {
  User: TRANSLATIONS.ADMIN_ROLES_ROLE_USER,
  Moderator: TRANSLATIONS.ADMIN_ROLES_ROLE_MODERATOR,
  Admin: TRANSLATIONS.ADMIN_ROLES_ROLE_ADMIN,
};

// Assign/revoke Admin or Moderator on other users -- Admin-only, never
// shown to a Moderator (see Admin.tsx's ADMIN_ONLY_SECTIONS and the
// backend's AdminRoles.Admin-only guard on AdminRolesController). A role
// change is consequential enough (especially granting Admin) that it goes
// through a Modal.confirm before actually submitting, unlike a plain
// inline edit.
const RolesSection: React.FC = () => {
  const { t } = useTranslation();
  const [users, setUsers] = useState<IAdminRoleUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [search, setSearch] = useState<string | undefined>(undefined);
  const [savingUserId, setSavingUserId] = useState<string | undefined>(undefined);

  const loadUsers = useCallback(() => {
    setLoading(true);
    setError(false);
    getAdminRoleUsers(search)
      .then(setUsers)
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [search]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const applyRoleChange = async (userId: string, role: string) => {
    setSavingUserId(userId);
    try {
      await setAdminUserRole(userId, role);
      message.success(translate(t, TRANSLATIONS.ADMIN_ACTION_SUCCESS));
      loadUsers();
    } catch {
      message.error(translate(t, TRANSLATIONS.ADMIN_ACTION_ERROR));
    } finally {
      setSavingUserId(undefined);
    }
  };

  const confirmRoleChange = (record: IAdminRoleUser, role: string) => {
    const roleLabel = translate(t, ROLE_LABEL_KEYS[role]);
    Modal.confirm({
      title: translate(t, TRANSLATIONS.ADMIN_ROLES_CHANGE_CONFIRM_TITLE),
      content: `${translate(t, TRANSLATIONS.ADMIN_ROLES_CHANGE_CONFIRM_CONTENT)} "${roleLabel}"?`,
      onOk: () => applyRoleChange(record.userId, role),
    });
  };

  return (
    <div>
      <Title level={4} style={{ marginBottom: 8 }}>
        {translate(t, TRANSLATIONS.ADMIN_NAV_ROLES)}
      </Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
        {translate(t, TRANSLATIONS.ADMIN_ROLES_DESCRIPTION)}
      </Text>

      <Space style={{ marginBottom: 16 }} wrap>
        <Search
          allowClear
          placeholder={translate(t, TRANSLATIONS.ADMIN_ROLES_SEARCH_PLACEHOLDER)}
          style={{ maxWidth: 280, width: '100%' }}
          onSearch={(value) => setSearch(value || undefined)}
        />
      </Space>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 32 }}>
          <Spin />
        </div>
      ) : error ? (
        <Alert type="error" showIcon message={translate(t, TRANSLATIONS.ADMIN_ROLES_LOAD_ERROR)} />
      ) : users.length === 0 ? (
        <EmptyState
          icon={<IconShieldCog size={22} />}
          title={translate(t, TRANSLATIONS.ADMIN_ROLES_EMPTY_TITLE)}
          description={translate(t, TRANSLATIONS.ADMIN_ROLES_EMPTY_DESCRIPTION)}
        />
      ) : (
        <AdminResponsiveList<IAdminRoleUser>
          rowKey="userId"
          dataSource={users}
          pagination={{ pageSize: 20 }}
          scroll={{ x: 'max-content' }}
          toCard={(record) => ({
            key: record.userId,
            title: record.displayName,
            subtitle: record.email,
            fields: [{ label: translate(t, TRANSLATIONS.ADMIN_ROLES_COL_CREATED), value: new Date(record.createdAt).toLocaleDateString() }],
            // The role select IS the action here -- there is no separate
            // button to press, so it sits where the actions do.
            actions: (
              <Select
                size="small"
                style={{ width: 160 }}
                value={record.role}
                loading={savingUserId === record.userId}
                disabled={savingUserId === record.userId}
                onChange={(role) => confirmRoleChange(record, role)}
                options={ROLE_OPTIONS.map((role) => ({ value: role, label: translate(t, ROLE_LABEL_KEYS[role]) }))}
              />
            ),
          })}
          columns={[
            {
              title: translate(t, TRANSLATIONS.ADMIN_ROLES_COL_NAME),
              dataIndex: 'displayName',
              key: 'displayName',
              sorter: textSorter<IAdminRoleUser>('displayName'),
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_ROLES_COL_EMAIL),
              dataIndex: 'email',
              key: 'email',
              sorter: textSorter<IAdminRoleUser>('email'),
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_ROLES_COL_CREATED),
              dataIndex: 'createdAt',
              key: 'createdAt',
              sorter: dateSorter<IAdminRoleUser>('createdAt'),
              render: (value: string) => new Date(value).toLocaleDateString(),
            },
            {
              title: translate(t, TRANSLATIONS.ADMIN_ROLES_COL_ROLE),
              dataIndex: 'role',
              key: 'role',
              render: (value: string, record: IAdminRoleUser) => (
                <Select
                  size="small"
                  style={{ maxWidth: 140, width: '100%' }}
                  value={value}
                  loading={savingUserId === record.userId}
                  disabled={savingUserId === record.userId}
                  onChange={(role) => confirmRoleChange(record, role)}
                  options={ROLE_OPTIONS.map((role) => ({ value: role, label: translate(t, ROLE_LABEL_KEYS[role]) }))}
                />
              ),
            },
          ]}
        />
      )}
    </div>
  );
};

export default RolesSection;
